import { Box, TablePagination, Typography } from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_RowData,
  MRT_VisibilityState,
  MRT_ColumnSizingState,
  MaterialReactTable,
  MRT_SortingState,
  MRT_ColumnOrderState,
  MRT_ExpandedState,
  MaterialReactTableProps,
} from "material-react-table";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

const styles = {
  tableHeadCell: {
    backgroundColor: "secondary.main",
    color: "white",
  },
  tableBodyCell: {
    whiteSpace: "wrap",
  },
  pagination: { ml: "auto" },
  wrapper: {
    // Keeps the table out of the layout's intrinsic width, which otherwise makes the whole page
    // scroll sideways by the width of the left nav. Do not replace with width: 100%.
    width: 0,
    minWidth: "100%",
    ".Mui-TableHeadCell-Content-Wrapper": { whiteSpace: "nowrap" },
    ".MuiCollapse-root": { width: "100%" },
  },
};

export const DEFAULT_ROWS_PER_PAGE = 10;
export type TableSortType = "desc" | "asc";

type ConfigurableTableProps<T extends MRT_RowData> = {
  isLoading?: boolean;
  title?: string;
  settingsId: string;
  columns: MRT_ColumnDef<T>[];
  data: T[];
  // Filter-driven column visibility that wins over (and never persists to) the
  // operator's localStorage map. undefined => today's behavior.
  forcedColumnVisibility?: MRT_VisibilityState;
  // Initial column visibility; applies only when the operator has no saved
  // localStorage map yet. undefined => all columns visible.
  defaultColumnVisibility?: MRT_VisibilityState;
  // Materialize pagination + sorting client-side (for already-assembled rows).
  // undefined => today's server-driven manual mode.
  enableClientDataOps?: boolean;
  // Rows are expanded by index, so a new value here (e.g. regrouped data) collapses them all.
  expandedResetKey?: unknown;
  paginationSettings?: {
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    onOrderChange: (direction: TableSortType) => void;
    onOrderByChange: (column: string) => void;
    rowCount?: number;
  };
} & Pick<
  MaterialReactTableProps<T>,
  | "renderDetailPanel"
  | "enableExpanding"
  | "enableExpandAll"
  | "getRowCanExpand"
  | "renderBottomToolbar"
  | "initialState"
  | "muiTableBodyRowProps"
>;

export default function ConfigurableTable<T extends MRT_RowData>({
  isLoading,
  title,
  settingsId,
  columns,
  data,
  forcedColumnVisibility,
  defaultColumnVisibility,
  enableClientDataOps,
  expandedResetKey,
  paginationSettings,
  ...rest
}: ConfigurableTableProps<T>) {
  const { formatMessage: t } = useIntl();
  const isFirstRender = useRef(true);

  const hasPaginationCallbacks = !!(
    paginationSettings?.onPageChange && paginationSettings?.onPageSizeChange
  );

  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [columnOrder, setColumnOrder] = useState<MRT_ColumnOrderState>([]);
  const [columnSizing, setColumnSizing] = useState<MRT_ColumnSizingState>({});
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    defaultColumnVisibility ?? {}
  );
  const [expanded, setExpanded] = useState<MRT_ExpandedState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_ROWS_PER_PAGE,
  });

  // Before paint, so the old index map never opens a row of the new data.
  useLayoutEffect(() => setExpanded({}), [expandedResetKey]);

  useEffect(() => {
    paginationSettings?.onPageChange?.(pagination.pageIndex);
    paginationSettings?.onPageSizeChange?.(pagination.pageSize);
  }, [pagination, paginationSettings]);

  useEffect(() => {
    if (enableClientDataOps) return;
    if (paginationSettings && sorting[0]) {
      paginationSettings.onOrderChange(sorting[0].desc ? "desc" : "asc");
      paginationSettings.onOrderByChange(sorting[0].id);
    }
  }, [sorting, paginationSettings, enableClientDataOps]);

  useEffect(() => {
    const sort = localStorage.getItem(`${settingsId}_sorting`);
    const order = localStorage.getItem(`${settingsId}_columnOrder`);
    const size = localStorage.getItem(`${settingsId}_columnSizing`);
    const visibility = localStorage.getItem(`${settingsId}_columnVisibility`);

    sort && setSorting(JSON.parse(sort));
    order && setColumnOrder(JSON.parse(order));
    size && setColumnSizing(JSON.parse(size));
    visibility && setColumnVisibility(JSON.parse(visibility));

    isFirstRender.current = false;
  }, [settingsId]);

  useEffect(() => {
    !isFirstRender.current &&
      localStorage.setItem(`${settingsId}_sorting`, JSON.stringify(sorting));
  }, [sorting, settingsId]);

  useEffect(() => {
    !isFirstRender.current &&
      localStorage.setItem(
        `${settingsId}_columnOrder`,
        JSON.stringify(columnOrder)
      );
  }, [columnOrder, settingsId]);

  useEffect(() => {
    !isFirstRender.current &&
      localStorage.setItem(
        `${settingsId}_columnSizing`,
        JSON.stringify(columnSizing)
      );
  }, [columnSizing, settingsId]);

  useEffect(() => {
    !isFirstRender.current &&
      localStorage.setItem(
        `${settingsId}_columnVisibility`,
        JSON.stringify(columnVisibility)
      );
  }, [columnVisibility, settingsId]);

  const effectiveColumnVisibility = {
    ...columnVisibility,
    ...forcedColumnVisibility,
  };

  return (
    <Box sx={styles.wrapper}>
      <MaterialReactTable
        {...rest}
        columns={columns}
        data={data}
        renderTopToolbarCustomActions={() => (
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        )}
        enableFilters={false}
        enableDensityToggle={false}
        enableColumnResizing={true}
        enableColumnOrdering={true}
        enableColumnActions={false}
        enablePagination={enableClientDataOps || hasPaginationCallbacks}
        enableFullScreenToggle={false}
        onSortingChange={setSorting}
        onColumnOrderChange={setColumnOrder}
        onColumnSizingChange={setColumnSizing}
        onColumnVisibilityChange={(updater) => {
          setColumnVisibility((prev) => {
            const base = { ...prev, ...forcedColumnVisibility };
            const next = typeof updater === "function" ? updater(base) : updater;
            const stripped = { ...next };
            for (const key of Object.keys(forcedColumnVisibility ?? {})) {
              delete stripped[key];
            }
            return stripped;
          });
        }}
        onPaginationChange={setPagination}
        onExpandedChange={setExpanded}
        state={{
          expanded,
          columnVisibility: effectiveColumnVisibility,
          columnSizing,
          sorting,
          columnOrder,
          pagination,
          isLoading,
          density: "compact",
        }}
        muiTableBodyCellProps={{ sx: styles.tableBodyCell }}
        muiColumnDragHandleProps={{ sx: styles.tableHeadCell }}
        muiExpandAllButtonProps={{ sx: styles.tableHeadCell }}
        muiExpandButtonProps={({ row, table }) => {
          // Same rule MRT uses to enable the button: expandable rows or a detail panel to show.
          const canExpand =
            row.getCanExpand() || !!table.options.renderDetailPanel?.({ row, table });
          return {
            "aria-label": t({ id: row.getIsExpanded() ? "collapse" : "expand" }),
            "aria-expanded": row.getIsExpanded(),
            sx: canExpand ? undefined : { visibility: "hidden" },
          };
        }}
        manualPagination={hasPaginationCallbacks && !enableClientDataOps}
        manualSorting={!enableClientDataOps && !!paginationSettings}
        rowCount={paginationSettings?.rowCount}
        // MUI's own pagination, as in LazyTable: MRT's summary hardcodes a hyphen range.
        positionPagination="none"
        renderBottomToolbarCustomActions={({ table }) => {
          const { pageIndex, pageSize } = table.getState().pagination;
          return (
            table.options.enablePagination && (
              <TablePagination
                component="div"
                sx={styles.pagination}
                count={table.getRowCount()}
                page={pageIndex}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                labelRowsPerPage={t({ id: "tableRowsPerPage" })}
                onPageChange={(_, page) => table.setPageIndex(page)}
                onRowsPerPageChange={(e) => table.setPageSize(+e.target.value)}
              />
            )
          );
        }}
      />
    </Box>
  );
}
